<template>
    <wrapper-features-templates-one-column
    buttonFullWidth
    :hideWhiteBoxOnLoad="showLoader || (!getAvailableProducts.products.length && !showLoader && !applyErrorState)"
    :showLoader="showLoader"
    :applyErrorState="applyErrorState"
	:showFooter="!showLoader && !applyErrorState && getMobileAppState && getAvailableProducts.products.length"
	:footerButtonsParams="footerButtonsData"
    @backOrCloseClicked="emit('backOrCloseClicked')">
        <transition-group name="common-enter-animation" mode="out-in" appear>
            <card-product-selector-wrapper
            v-if="!showLoader && !applyErrorState && getAvailableProducts.products.length"
            :applyMobileHeaderProductSlider="getMobileAppState"
            :productsList="parsedProductList"
            @updateSelectedProductIndex="mobileSelectedIndex = $event"/>
        </transition-group>
        <!-- Empty State -->
        <template #empty-state-slot>
            <empty-state-generic
            v-if="!showLoader && !applyErrorState && !getAvailableProducts.products.length"
            illustration="emptyState/savings"
            :title="t('savings.products.productList.emptyStates.title')"
            :description="t('savings.products.productList.emptyStates.description')"/>
        </template>
    </wrapper-features-templates-one-column>
    <!-- Error Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOneColumn from '@/components/wrappers/featuresTemplates/OneColumn'
import CardProductSelectorWrapper from '@/components/generic/cards/productSelector/SelectorsWrapper'
import ToastGeneric from '@/components/generic/toasts/Generic'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'

/* Assets and Helpers */
import { amountFormat } from '@/assets/scripts/amounts'
import { navigateByName } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue */
import { ref, computed, onBeforeUnmount } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* mapActions & mapGetters */
const {	availableProductsFetch, clearProductsListFeatureStates } = mapActions(moduleMapper.SAVINGS_PRODUCTS)
const { getAvailableProducts } = mapGetters(moduleMapper.SAVINGS_PRODUCTS)
const { getMobileAppState } = mapGetters()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* Data */
const showLoader = ref(false)
const applyErrorState = ref(false)
const mobileSelectedIndex = ref(0)

/* Footer Buttons Data */
const footerButtonsData = computed(() => {
	return [{
		label: t('savings.products.productList.constituteTextBtn'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		callbackFunction: () => {
            const selectedProduct = getAvailableProducts.value.products[mobileSelectedIndex.value]
            navigateToSavingsConstitute(selectedProduct)
        }
	}]
})

const navigateToSavingsConstitute = (selectedProduct) => {
    navigateByName(routeList.SAVINGS_PRODUCTS_CONSTITUTE_NAME, { params: getAvailableProducts.value.products.find(el => el.id === selectedProduct.id) })
}

const parsedProductList = computed(() => {
    if (!getAvailableProducts.value) return

	const productList = []

	getAvailableProducts.value.products.forEach(product => {
        const durationDays = {
            text: product.maximumDuration === product.minimumDuration
                ? t('savings.products.productList.durationContent', { numbersOfDays: product.minimumDuration })
                : t('savings.products.productList.durationContentWithRange', { minimumOfDays: product.minimumDuration, maximumOfDays: product.maximumDuration })
        }

        const infoSheetClicked = () => {
            window.open(product.infoSheetUrl, '_blank')
        }

        productList.push({
            id: product.id,
            currency: product.currency.name,
            name: product.name,
            title: product.name,
            description: product.description,
            shortDescription: product.shortDescription,
            infoSheetUrl: product.infoSheetUrl,
            infoSheetUrlBtnText: t('savings.products.productList.infoSheetBtn'),
            infoSheetCustomSuccessMessage: t('savings.products.productList.downloadInfoSheetSuccessMessage'),
            secondBtnText: t('savings.products.productList.constituteTextBtn'),
            secondBtnClicked: navigateToSavingsConstitute,
            textBlocks: [
                /* Duration */
                {
                    label: t('savings.products.productList.durationLabel'),
                    content: product.maximumDuration && product.minimumDuration !== '' ? durationDays.text : '-'
                },
                /* TANB */
                {
                    label: t('savings.products.productList.tanbLabel'),
                    content: product.interestRateDescription || '-'
                },
                /* Minimum Amount */
                {
                    label: t('savings.products.productList.minimumAmountLabel'),
                    content: product.minimumAmount && product.currency.name !== ''
                    ? amountFormat(product.minimumAmount, product.currency.name)
                    : '-'
                },
                /* Maximum Amount */
                {
                    label: t('savings.products.productList.maximumAmountLabel'),
                    content: product.maximumAmount && product.currency.name !== ''
                    ? amountFormat(product.maximumAmount, product.currency.name)
                    : '-'
                }
            ],
            disclaimer: product.disclaimer,
            hasPlafond: product.hasPlafond,
            duration: {
                label: t('savings.products.productList.durationLabel'),
                content: product.maximumDuration && product.minimumDuration !== '' ? durationDays.text : '-'
            },
            tanb: {
                label: t('savings.products.productList.tanbLabel'),
                content: product.interestRateDescription || '-'
            },
            minimumAmount: {
                label: t('savings.products.productList.minimumAmountLabel'),
                content: product.minimumAmount && product.currency.name !== ''
                ? amountFormat(product.minimumAmount, product.currency.name)
                : '-'
            },
            maximumAmount: {
                label: t('savings.products.productList.maximumAmountLabel'),
                content: product.maximumAmount && product.currency.name !== ''
                ? amountFormat(product.maximumAmount, product.currency.name)
                : '-'
            },
            infoSheetBtn: {
                buttonText: t('savings.products.productList.infoSheetBtn'),
                buttonType: 'primary',
                buttonIcon: 'download',
                link: product.infoSheetUrl,
                buttonClicked: infoSheetClicked
            },
            constituteBtn: {
                buttonText: t('savings.products.productList.constituteTextBtn'),
                buttonType: 'secondary',
                buttonClicked: navigateToSavingsConstitute
            },
            subProductCode: product.subProduct.code,
            productCode: product.product.code
        })
	})

	return productList
})

/*************************************************
*                                                *
*          Available Products List Request       *
*                                                *
*************************************************/
const initialRequest = async () => {
    showLoader.value = true
    applyErrorState.value = false

	try {
        await availableProductsFetch()
		showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequest)
	}
}

/*************************************************
*                                                *
*                 Error Feedback                 *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
    showLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn)
    showFeedbackToast.value = true
}

initialRequest()

onBeforeUnmount(() => {
    clearProductsListFeatureStates()
})
</script>

<style lang="scss" scoped>

.subscribe-content {
    @include commonEnteringAnimation;
    width: 100%;
    align-items: center;
}
</style>
